import Table from '@shared/components/Table';
import { useTranslation } from 'react-i18next';
import ActionButtons from '@shared/components/ActionButtons';
import { COMMON_BUTTONS } from '@config/actionButtonConfigs';
import { useEffect, useState } from 'react';
import OrgUnitPicker from '../../../shared/components/OrgUnitPicker';
import { useOrgUnitSearch } from './hooks/useOrgUnitSearch';
import { useSelector } from 'react-redux';
import useModal from '@shared/hooks/useModal';
import useFetchData from '@shared/hooks/useFetchData';
import { registerService } from '@shared/services/register';
import { personService } from '@shared/services/personService';
import { companiesService } from '@shared/services/companiesService';
import RegisterForm from './RegisterForm';
import ErrorModal from './ErrorModal';
import DeleteModal from '@shared/components/DeleteModal';
import PersonForm from '@modules/staff/person/PersonForm';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    clearPersonFormReturnContext,
    loadPersonFormReturnContext,
} from '@utils/personFormReturnStorage';

const REGISTER_FETCH_OPTIONS = { blockingError: true };
const REGISTER_DEFAULT_ACTIVE_FORM = 'employeeData';
const REGISTER_DEFAULT_ADDITIONAL_CONTRACT = 'false';
const REGISTER_DEFAULT_TERMINATED = 'false';

const buildRegisterFetchParams = ({
    pageNumber,
    pageSize,
    sortBy,
    sortDir,
    searchQuery,
    selectedAdditionalContract,
    selectedTerminated,
    overrides = {},
}) => ({
    page: pageNumber,
    pageSize,
    sortBy: sortBy || 'empId',
    sortDir: sortDir || 'asc',
    searchQuery,
    additionalContract: selectedAdditionalContract === 'true',
    terminated: selectedTerminated === 'true',
    ...overrides,
});

const isRegisterRecordDeletable = (record) => {
    const isEmpty = (value) => value == null || value === '';
    return isEmpty(record?.jobCode) && isEmpty(record?.jobName);
};

export default function Register() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { modalState, handleOpenModal, handleCloseModal } = useModal();
    const { pageNumber, fetchData, pageSize, sortBy, sortDir, data, totalPages, totalElements, setpageNumber, setPageSize, searchQuery, setSearchQuery, handleSearchClick } = useFetchData();

    const {
        showOrgUnitPicker,
        selectedOrgUnitLabel,
        openOrgUnitPicker,
        handleOrgUnitPickerCancel,
        handleOrgUnitSelect,
    } = useOrgUnitSearch({
        searchQuery,
        setSearchQuery,
        setpageNumber,
        onApplySearch: (nextSearchQuery) => {
            fetchData(registerService.getRegister, buildRegisterFetchParams({
                pageNumber: 0,
                pageSize,
                sortBy,
                sortDir,
                searchQuery: nextSearchQuery,
                selectedAdditionalContract,
                selectedTerminated,
            }), REGISTER_FETCH_OPTIONS);
        },
    });
    const [errorModalState, setErrorModalState] = useState({ visible: false, errorMessage: '', formData: null });
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [personData, setPersonData] = useState(null);
    const [loadingPerson, setLoadingPerson] = useState(false);
    const [restoredActiveForm, setRestoredActiveForm] = useState(REGISTER_DEFAULT_ACTIVE_FORM);
    const adminBranchId = useSelector((state) => state.auth.adminBranchId);
    const [selectedCompany, setSelectedCompany] = useState('');
    const [companyOptions, setCompanyOptions] = useState([]);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [selectedAdditionalContract, setSelectedAdditionalContract] = useState(REGISTER_DEFAULT_ADDITIONAL_CONTRACT);
    const [selectedTerminated, setSelectedTerminated] = useState(REGISTER_DEFAULT_TERMINATED);

    const handleAdditionalContractFilterChange = (value) => {
        setpageNumber(0);
        setSelectedAdditionalContract(value);
    };

    const handleTerminatedFilterChange = (value) => {
        setpageNumber(0);
        setSelectedTerminated(value);
    };

    // Keep company select in sync with the current admin branch.
    useEffect(() => {
        if (adminBranchId === null || adminBranchId === undefined) {
            setSelectedCompany('');
            setCompanyOptions([]);
        } else {
            const nextId = String(adminBranchId);
            setSelectedCompany(nextId);
        }
    }, [adminBranchId]);

    // Load admin branch company name for dropdown label.
    useEffect(() => {
        const fetchCompanyName = async () => {
            if (adminBranchId === null || adminBranchId === undefined) return;

            try {
                const company = await companiesService.getSelectedCompany(adminBranchId);
                const name = company?.name || company?.branchName || '';
                setCompanyOptions([{ id: adminBranchId, name }]);
            } catch (e) {
                console.error('Error fetching company name:', e);
                // Fallback: show id if name isn't available.
                setCompanyOptions([{ id: adminBranchId, name: String(adminBranchId) }]);
            }
        };

        fetchCompanyName();
    }, [adminBranchId]);

    useEffect(() => {
        fetchData(registerService.getRegister, buildRegisterFetchParams({
            pageNumber,
            pageSize,
            sortBy,
            sortDir,
            searchQuery,
            selectedAdditionalContract,
            selectedTerminated,
        }), REGISTER_FETCH_OPTIONS);
    }, [pageNumber, pageSize, sortBy, sortDir, selectedAdditionalContract, selectedTerminated]);

    // Restore PersonForm + tab when returning from other screen (Investment Services).
    useEffect(() => {
        let isCancelled = false;

        const restoreFromSession = async () => {
            try {
                const shouldRestoreFromExit = location.state?.restorePersonForm === true;
                if (!shouldRestoreFromExit) {
                    clearPersonFormReturnContext();
                    return;
                }

                const parsed = loadPersonFormReturnContext();
                if (!parsed || !parsed?.person) return;

                const restoredPerson = parsed.person;
                const restoredPersonId = restoredPerson?.id || restoredPerson?.personId;
                let nextPersonData = restoredPerson;

                // Recreate the same data shape as normal row selection.
                if (restoredPersonId) {
                    try {
                        const freshPerson = await personService.getPersonById(restoredPersonId);
                        nextPersonData = {
                            ...freshPerson,
                            recordId: restoredPerson.recordId,
                            empId: restoredPerson.empId || freshPerson.empId,
                            fullName: restoredPerson.fullName,
                            personContractType: restoredPerson.personContractType,
                            selectedMainContract: restoredPerson.selectedMainContract || parsed.selectedMainContract || '',
                            selectedContractId: restoredPerson.selectedContractId || parsed.selectedContractId || null,
                            personId: restoredPerson.personId || freshPerson.personId || restoredPersonId,
                        };
                    } catch (error) {
                        console.error('Error restoring person data from return context:', error);
                        // Fallback to the serialized person context.
                        nextPersonData = restoredPerson;
                    }
                }

                if (isCancelled) return;

                if (parsed.selectedRecord?.id) {
                    setSelectedRecord(parsed.selectedRecord);
                } else {
                    setSelectedRecord({ id: restoredPerson?.recordId ?? 'return' });
                }
                setPersonData(nextPersonData);
                setRestoredActiveForm(parsed.activeForm || REGISTER_DEFAULT_ACTIVE_FORM);
            } catch {
                // ignore malformed context
            } finally {
                clearPersonFormReturnContext();
            }
        };

        restoreFromSession();
        return () => {
            isCancelled = true;
        };
    }, [location.state]);

    const columns = [
        { key: 'empId', label: t('staff.register.table.headers.employeeNumber') },
        { key: 'idn', label: t('staff.register.table.headers.idn') },
        { key: 'fullName', label: t('staff.register.table.headers.employeeName') },
        { key: 'jobCode', label: t('staff.register.table.headers.positionCode') },
        { key: 'jobName', label: t('staff.register.table.headers.position') },
        { key: 'orgUnitCode', label: t('staff.register.table.headers.strucGroup') },
        { key: 'orgUnitName', label: t('staff.register.table.headers.structureName') },
        { key: 'photo', label: t('staff.register.table.headers.photo') },
    ];
    const searchOptions = [
        { key: 'name', label: 'firstName' },
        { key: 'fathersName', label: 'fathersName' },
        { key: 'surname', label: 'surname' },
        { key: 'idn', label: 'idn' },
        { key: 'empId', label: 'employeeNumber' },
        { key: 'orgUnitId', label: 'structuralGroup', type: 'orgUnitPicker' },
    ];
    const actionButtons = (
        <ActionButtons
            buttons={[
                {
                    ...COMMON_BUTTONS.ADD,
                    onClick: () => handleOpenModal('add')
                },
                {
                    type: 'nap',
                    iconImg: '/images/logo/NAP_2.png',
                    title: 'staff.register.nap',
                    showWhen: 'always',
                    iconProps: { style: { width: 24, height: 24 } },
                    onClick: () => { navigate('/personnel/employment-contracts/electronic-work') }
                }
            ]}
        />
    )

    const handleFormSubmit = async (formData) => {
        try {
            await registerService.createRegister(formData);
            notifySuccess(t('common.createdSuccessfully'));
            handleCloseModal();
            const params = buildRegisterFetchParams({
                pageNumber,
                pageSize,
                sortBy,
                sortDir,
                searchQuery,
                selectedAdditionalContract,
                selectedTerminated,
            });
            fetchData(registerService.getRegister, params, REGISTER_FETCH_OPTIONS);
        } catch (error) {
            console.error('Error saving record:', error);
            const errorMessage = error?.response?.data?.message || error?.response?.data || t('common.errorMessage');
            handleCloseModal();
            setErrorModalState({
                visible: true,
                errorMessage: errorMessage,
                formData: formData
            });
        }
    };

    const handleErrorModalSave = async () => {
        try {
            await registerService.createRegister(errorModalState.formData, { createAnother: true });
            notifySuccess(t('common.createdSuccessfully'));
            setErrorModalState({ visible: false, errorMessage: '', formData: null });
            const params = buildRegisterFetchParams({
                pageNumber,
                pageSize,
                sortBy,
                sortDir,
                searchQuery,
                selectedAdditionalContract,
                selectedTerminated,
            });
            fetchData(registerService.getRegister, params, REGISTER_FETCH_OPTIONS);
        } catch (error) {
            console.error('Error saving record with createAnother:', error);
            const errorMessage = error?.response?.data?.message || error?.response?.data || t('common.errorMessage');
            notifyError(errorMessage);
        }
    };

    const handleErrorModalClose = () => {
    setErrorModalState({ visible: false, errorMessage: '', formData: null });
    };

    const handleRowClick = async (index) => {
        const currentData = data?.content || data || [];
        if (index >= 0 && index < currentData.length) {
            setRestoredActiveForm(REGISTER_DEFAULT_ACTIVE_FORM);
            const record = currentData[index];
            setSelectedRecord(record);
            
            // Get personId from the clicked record (not the register record ID)
            let personId = record.personId || record.person?.id;
            
            
            // If personId is not directly available, try to find it by IDN
            if (!personId && record.idn) {
                try {
                    setLoadingPerson(true);
                    const searchResponse = await registerService.searchPersons({ idn: record.idn });
                    const personsList = searchResponse._embedded?.personsDTOList || searchResponse.content || (Array.isArray(searchResponse) ? searchResponse : []);
                    if (personsList && personsList.length > 0) {
                        personId = personsList[0].id;
                    }
                } catch (error) {
                    console.error('Error searching person by IDN:', error);
                }
            }
            
            if (personId) {
                setLoadingPerson(true);
                try {
                    const person = await personService.getPersonById(personId);
                    const personWithEmpData = {
                        ...person,
                        recordId: record.id,
                        empId: record.empId || person.empId,
                        fullName: record.fullName,
                        personId: record.personId || personId || person.personId || person.id
                    };
                    setPersonData(personWithEmpData);
                } catch (error) {
                    console.error('Error fetching person data:', error);
                    notifyError(t('common.errorMessage'));
                    setPersonData(null);
                    setSelectedRecord(null);
                } finally {
                    setLoadingPerson(false);
                }
            } else {
                setPersonData(null);
                setLoadingPerson(false);
            }
        }
    };

    const handleRecordModalClose = () => {
        setSelectedRecord(null);
        setPersonData(null);
        setRestoredActiveForm(REGISTER_DEFAULT_ACTIVE_FORM);
    };

    const refetchRegister = () => {
        fetchData(registerService.getRegister, buildRegisterFetchParams({
            pageNumber,
            pageSize,
            sortBy,
            sortDir,
            searchQuery,
            selectedAdditionalContract,
            selectedTerminated,
        }), REGISTER_FETCH_OPTIONS);
    };

    const handleDeleteRecordClick = (record) => {
        setRecordToDelete(record);
    };

    const handleConfirmDeleteRecord = async () => {
        if (!recordToDelete?.id) {
            return;
        }

        try {
            await registerService.deleteEmp(recordToDelete.id);
            notifySuccess(t('common.deletedSuccessfully'));
            setRecordToDelete(null);
            refetchRegister();
        } catch (error) {
            console.error('Error deleting register record:', error);
            notifyError(error?.response?.data?.message || t('common.errorMessage'));
        }
    };

    return (
        <>
            <Table
                title={t('staff.register.title')}
                columns={columns}
                searchOptions={searchOptions}
                showPageSizeSelector={false}
                showCompanySelect={true}
                showAdditionalContractSelect={true}
                showTerminatedSelect={true}
                selectedAdditionalContract={selectedAdditionalContract}
                setSelectedAdditionalContract={handleAdditionalContractFilterChange}
                selectedTerminated={selectedTerminated}
                setSelectedTerminated={handleTerminatedFilterChange}
                companyOptions={companyOptions}
                selectedCompany={selectedCompany}
                setSelectedCompany={setSelectedCompany}
                actionButtons={actionButtons}
                showDeleteIcon={true}
                shouldShowDeleteIcon={isRegisterRecordDeletable}
                onDeleteIconClick={handleDeleteRecordClick}
                data={data}
                getData={() => {
                    fetchData(registerService.getRegister, buildRegisterFetchParams({
                        pageNumber,
                        pageSize,
                        sortBy,
                        sortDir,
                        searchQuery,
                        selectedAdditionalContract,
                        selectedTerminated,
                    }), REGISTER_FETCH_OPTIONS);
                }}
                totalPages={totalPages}
                totalElements={totalElements}
                pageNumber={pageNumber}
                pageSize={pageSize}
                setpageNumber={setpageNumber}
                setPageSize={setPageSize}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                handleSearchClick={() => {
                    handleSearchClick(registerService.getRegister, buildRegisterFetchParams({
                        pageNumber: 0,
                        pageSize,
                        sortBy,
                        sortDir,
                        searchQuery,
                        selectedAdditionalContract,
                        selectedTerminated,
                    }), REGISTER_FETCH_OPTIONS);
                }}
                showCustomSearch={showOrgUnitPicker}
                customSearchComponent={
                    showOrgUnitPicker ? (
                        <OrgUnitPicker
                            onSelect={handleOrgUnitSelect}
                            onCancel={handleOrgUnitPickerCancel}
                        />
                    ) : null
                }
                searchBarProps={{
                    onOrgUnitPickerOpen: openOrgUnitPicker,
                    orgUnitDisplayLabel: selectedOrgUnitLabel,
                }}
                handleRowClick={handleRowClick}
                renderCell={(item, column) => {
                    if (column.key === 'fullName') {
                        const fullName = item[column.key];
                        if (!fullName) return '';
                        // Replace "null" (case-insensitive) with empty string and clean up spaces
                        return String(fullName)
                            .replace(/\bnull\b/gi, '')
                            .replace(/\s+/g, ' ')
                            .trim();
                    }

                    if (column.key === 'photo') {
                        if (!item.photo || !item.personId) {
                            return '';
                        }

                        return (
                            <img
                                src="/images/icons/photo.png"
                                alt={t('staff.register.table.headers.photo')}
                                title={t('staff.register.table.headers.photo')}
                                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                        const photoResponse = await personService.getPersonPicture(item.personId);
                                        if (!photoResponse?.data) {
                                            notifyError(t('common.errorMessage'));
                                            return;
                                        }
                                        const photoUrl = URL.createObjectURL(photoResponse.data);
                                        window.open(photoUrl, '_blank', 'noopener,noreferrer');
                                    } catch (error) {
                                        console.error('Error opening person photo:', error);
                                        notifyError(t('common.errorMessage'));
                                    }
                                }}
                            />
                        );
                    }

                    return item[column.key];
                }}
            />
            {modalState.visible && (modalState.type === 'add') && (
                <RegisterForm
                    onClose={handleCloseModal}
                    onConfirm={handleFormSubmit}
                />
            )}
            {errorModalState.visible && (
                <ErrorModal
                    onClose={handleErrorModalClose}
                    onSave={handleErrorModalSave}
                    errorMessage={errorModalState.errorMessage}
                />
            )}
            {recordToDelete && (
                <DeleteModal
                    onClose={() => setRecordToDelete(null)}
                    onConfirm={handleConfirmDeleteRecord}
                    name={recordToDelete.fullName || recordToDelete.empId || ''}
                    entityType="record"
                />
            )}
            {selectedRecord && loadingPerson && (
                <div className="modal show" tabIndex="-1">
                    <div className="person-form-wrapper">
                        <p>{t('common.loading')}</p>
                    </div>
                </div>
            )}
            {selectedRecord && personData && (
                <PersonForm
                    onClose={handleRecordModalClose}
                    person={personData}
                    selectedData={personData?.id || null}
                    fetchData={() => {
                        fetchData(registerService.getRegister, buildRegisterFetchParams({
                            pageNumber,
                            pageSize,
                            sortBy,
                            sortDir,
                            searchQuery,
                            selectedAdditionalContract,
                            selectedTerminated,
                        }), REGISTER_FETCH_OPTIONS);
                    }}
                    showRegisterIcons={true}
                    initialActiveForm={restoredActiveForm}
                />
            )}
        </>
    );
}